<template lang="pug">
.author-home.flex.flex-col.h-screen.w-screen.bg-white.dark_bg-gray-700
  .flex.gap-2.p-2.z-50.bg-gray-100.dark_bg-gray-800.h-14.items-center
    MainMenu
      .flex.flex-col
        a.text-gray-900.group.rounded-md.p-2.text-sm.hover_bg-primary-100(
          href="#/home"
        )
          | Home – all courses

        a.text-gray-900.group.rounded-md.p-2.text-sm.hover_bg-primary-100(
          :href="firmCode ? `#/editor/${firmCode}_${courseId}` : `#/editor/${courseId}`"
        )
          | Edit #[b {{ baseYamlFilename }}]
          
        a.text-gray-900.group.rounded-md.p-2.text-sm.hover_bg-primary-100(
          :href="firmCode ? `#/history/${firmCode}_${courseId}` : `#/history/${courseId}`"
        )
          | View #[b {{ baseYamlFilename }}] history
    .ms-auto
    ToggleDark

  .flex-1.p-24.overflow-y-auto.overflow-x-visible
    .max-w-screen-2xl.mx-auto
      .prose.prose.dark_prose-invert.max-w-none
          h1.font-normal 
           span Export transcript for <b>{{ baseYamlFilename }} </b>  

          p.opacity-80 
            span(v-if="title")
             a(:href="`#/editor/${baseYamlFilename.slice(0,-4)}`") {{ title }} 
             |  
             | &ndash;
            span(v-if="lastUpdated") 
             | Last updated 
             |  
             a(:href="`#/history/${baseYamlFilename.slice(0,-4)}`") {{ formatTimeAgo(lastUpdated) }}
            
          template(v-if="loadCourseError")
            p.text-xl.italic 🚨 {{ loadCourseError }}

          form(v-else-if="hasSavedYaml").p-4.my-6.bg-gray-100.dark_bg-gray-800.bg-opacity-10.rounded-lg.prose.prose.dark_prose-invert

            fieldset(
              :disabled="statuses.createTranscript === 'pending'"
            )
              h2.p-0.mt-0
                span Current course languages: {{ courseLanguages.length }}
                span(v-if="deprecatedCount > 0") &nbsp;({{ deprecatedCount }} deprecated)
              
              template(v-if="!hasSavedYaml")
                p.text-xl.italic 🚨 No course title found.
              template(v-else)
              .prose.dark_prose-invert
                .grid.grid-cols-4.gap-2
                  label.inline-flex.items-center.gap-1(
                    :class="{'p-2 bg-red-600/40': isDeprecated(l)}"
                    v-for="l in courseLanguages"
                  )
                    input(
                      name="lang"
                      type="radio"
                      :value="l"
                      @change="resetAudio"
                      v-model="selectedLanguage"
                    )
                    span {{ languageData[l].english }} ({{ l }})
                    abbr.mx-0.-mt-2.no-underline.border-b.border-dotted.scale-75.text-xl.font-bold(title="Deprecated language") 
                      span(v-if="isDeprecated(l)") [?]

          template(v-if="!loadCourseError")
            form.p-4.my-6.bg-gray-100.dark_bg-gray-800.bg-opacity-10.rounded-lg.prose.prose.dark_prose-invert
              fieldset(
                :disabled="statuses.createTranscript === 'pending'"
              )
                h2.p-0.mt-0 Settings

                .flex.gap-4
                  
                  label.inline-flex.items-center.gap-1
                    input(
                      name="fileType"
                      type="radio"
                      value="docx"
                      v-model="fileType"
                    )
                    span Docx
                  
                  label.inline-flex.items-center.gap-1
                    input(
                      name="fileType"
                      type="radio"
                      value="pdf"
                      v-model="fileType"
                      :disabled="isUnsupportedPdfLanguage()"
                    )
                    span PDF
                
                //- p
                //-   label.inline-flex.items-center.gap-1
                //-     input(
                //-       name="lang"
                //-       type="checkbox"
                //-       :value="true"
                //-       v-model="highlightAnswers"
                //-     )
                //-     span Highlight correct answers
                
                p
                  label.inline-flex.items-center.gap-1.w-full
                    span.pe-1 Tags:
                    input(
                      class="w-[50ch]"
                      name="tagsInput"
                      type="text"
                      value=""
                      v-model="tags"
                      placeholder="tag1+tag2"
                      title="Leave empty to include all tagged content"
                    )

            form.p-4.my-6.bg-gray-100.dark_bg-gray-800.bg-opacity-10.rounded-lg.prose.prose.dark_prose-invert(v-if="selectedLanguage")
              h2.p-0.mt-0 Export {{ languageData[selectedLanguage].english }} transcript
              
              fieldset(:disabled="statuses.createTranscript === 'pending'")
                p
                  button.btn-primary.ms-auto.btn-hover_cursor-pointer.bg-primary-600.btn-hover_bg-primary-700.disabled_bg-gray-600.disabled_opacity-80.group.text-white.text-center.font-bold.py-2.px-4.rounded(
                    title="Create a course transcript document"
                    @click="createCourseTranscript(fileType)"
                  )
                    Icon.inline-block.me-2.text-2xl.animate-spin(:icon="'ph:spinner'" v-if="statuses.createTranscript === 'pending'")
                    Icon.inline-block.me-2.text-2xl(icon="file-icons:microsoft-word" v-if="fileType === 'docx'")
                    Icon.inline-block.me-2.text-2xl(icon="fluent:document-pdf-32-regular" v-else-if="fileType === 'pdf'")
                    span.prose-lg Export
                  
                p(
                  v-if="statuses.createTranscript === 'error'"
                )
                  span.text-red-700.dark_text-red-400.font-bold {{ errors.createTranscript }}

    iframe.hidden(
      ref="courseTranscriptIFrame"
      referrerpolicy="origin-when-cross-origin"
      style="width: 800px; height: 600px; border: 0;"
      v-if="!loadCourseError"
    )

</template>

<script setup lang="ts">
import MainMenu from './MainMenu.vue';
import { formatTimeAgo } from '@vueuse/core';
import ToggleDark from './ToggleDark.vue';
import useDocTitle from '../lib/useDocTitle';
import { onMounted, ref, computed, reactive, watch, unref } from 'vue';
import { loadCourse } from '../store/courses';
import { getLatestSaved } from '../lib/callModContentAuthors';
import { Icon } from '@iconify/vue';
import jsYaml from 'js-yaml';
import extractBaseCourse from '../../lib/extractBaseCourse';
import languageData from '../../lib/languages.json';
import activeLanguages from '../../lib/activeLanguages.yml';
import removeMarkdown from '../../../esm/lib/removeMarkdown.js';

const props = defineProps<{ courseId: number; firmCode?: string }>();
const courseLanguages = ref([]);
const selectedLanguage = ref('');
const title = ref<string>();
const hasSavedYaml = ref(false);
const loadCourseError = ref('');
const lastUpdated = ref<Date>();
const course = ref();
const courseTranscriptIFrame = ref<HTMLIFrameElement>();
const courseAndFirm = {
  courseId: props.courseId,
  firmCode: props.firmCode
};
const fileType = ref('docx');
const highlightAnswers = ref(false);
const tags = ref('');

const baseLanguage = computed(() => {
  if (selectedLanguage.value.indexOf('-') !== -1) {
    const splitLang = selectedLanguage.value.split(/-|_/);
    return splitLang[0].toLowerCase();
  } else {
    return selectedLanguage.value.toLowerCase();
  }
});

const copyToClipboard = async text => {
  return await navigator.clipboard.writeText(text);
};

const statuses = reactive<{
  [name: string]: 'idle' | 'pending' | 'success' | 'error';
}>({
  createTranscript: 'idle'
});

const errors = reactive<{
  [name: string]: string;
}>({
  createTranscript: 'ERROR!'
});

const baseYamlFilename = computed(() =>
  props.firmCode
    ? `${props.firmCode}_${props.courseId}.yml`
    : `${props.courseId}.yml`
);

const languages = Object.keys(languageData)
  .filter(code => activeLanguages?.includes(code))
  .filter(code => !languageData[code].deprecated)
  .map(code => ({
    code,
    ...languageData[code]
  }));

const deprecatedLanguages = Object.keys(languageData)
  .filter(code => languageData[code].deprecated)
  .map(code => ({
    code,
    ...languageData[code]
  }));

const isDeprecated = lang => {
  return deprecatedLanguages.some(l => l.code === lang);
};

const deprecatedCount = computed(() => {
  return courseLanguages.value.filter(isDeprecated).length;
});

// any languages without font support in the PDF library (specified in languages.json)
const isUnsupportedPdfLanguage = () => {
  return languageData?.[selectedLanguage.value]?.transcriptFormat === 'docx';
};

// strip lines breaks and forced line break backslashes from the course title when used in the nav menu/bookmarks
const removeLinebreaks = text => {
  return text
    .replace(/ *\\?\n/g, ' ')
    .replace(/{r}/g, ' ')
    .replace(/{n}/g, ' ')
    .replace(/\n+/g, ' ')
    .replace(/\s+/g, ' ');
};

const url = computed(() => {
  let { latest } = course.value;
  const { courseId, firmCode, ubercourseVersion } = latest;
  const courseQ = `courseid=${courseId}`;
  const firmQ = firmCode ? `firm=${firmCode}` : '';
  const langQ = `lang=${selectedLanguage.value}`;
  const wordQ = 'word=1';
  const tagQ =
    'tags=worddoc' +
    (tags.value ? `+${tags.value}` : '+includeall') +
    (highlightAnswers.value ? '+highlightanswers' : '');
  const queryString = ['turbo=999999', courseQ, firmQ, langQ, wordQ, tagQ].join(
    '&'
  );
  const localServer = window.location.origin.includes('local');
  const portalOrigin = new URL(window.location.href).origin;

  return localServer
    ? `./app.html?${queryString}`
    : `${portalOrigin}/latest/app.html?${queryString}`;
});

// @ts-ignore
const createCourseTranscript = async fileType => {
  statuses.createTranscript = 'pending';

  const firmCode = courseAndFirm.firmCode;
  const courseId = courseAndFirm.courseId;
  const iframe = courseTranscriptIFrame.value;

  if (!iframe) {
    throw new Error('Transcript iframe not found - please try again');
  }

  console.log(
    'Creating transcript:',
    courseId,
    firmCode,
    selectedLanguage.value,
    url.value
  );

  // Set up load promise before changing src
  const loadPromise = new Promise((resolve, reject) => {
    const handleLoad = () => {
      console.log('Iframe loaded successfully');
      iframe.removeEventListener('load', handleLoad);
      resolve(true);
    };

    const handleError = (error: ErrorEvent) => {
      console.error('Iframe load error:', error);
      iframe.removeEventListener('error', handleError);
      reject(new Error('Failed to load iframe content'));
    };

    iframe.addEventListener('load', handleLoad);
    iframe.addEventListener('error', handleError);
  });

  // Set iframe source
  iframe.src = url.value;

  console.log('Loading transcript iframe with URL:', iframe.src);

  // Wait for load
  await loadPromise;

  // Wait for pages element to be available
  const pages = await new Promise<HTMLElement>((resolve, reject) => {
    const checkForPages = () => {
      const cw = iframe.contentWindow as Window;
      const pages = cw.document.documentElement.querySelector(
        '.pages'
      ) as HTMLElement;
      if (pages) {
        resolve(pages);
      } else {
        setTimeout(checkForPages, 500);
      }
    };
    checkForPages();
  });

  let { latest } = course.value;

  // in the saved course data the title is the combined title and subtitle
  const courseTitle = latest.title
    ? removeMarkdown(removeLinebreaks(latest.title)).trim()
    : 'Course';

  let fileName = `${courseTitle} [Transcript ${
    firmCode ? firmCode + '_' : ''
  }${courseId} ${selectedLanguage.value}]`;

  // replace colons
  fileName = fileName.replace(/[:]/g, ' - ');
  // replace any invalid characters in the filename with a space
  fileName = fileName.replace(/[<>"/\\|?*\x00-\x1F]/g, ' ');
  // replace multiple underscores with a single one
  fileName = fileName.replace(/__+/g, '_');
  // replace multiple spaces with a single one
  fileName = fileName.replace(/\s+/g, ' ');

  // Generate word/pdf document
  const { default: createTranscript } =
    await import('../../lib/createTranscript');
  await createTranscript(
    pages,
    selectedLanguage.value,
    courseTitle,
    fileName,
    fileType
  )
    .then(() => {
      statuses.createTranscript = 'success';
    })
    .catch(e => {
      statuses.createTranscript = 'error';
      errors.createTranscript = e.message;
      console.error('Failed to generate transcript:', e.message);
    });
};

const load = async () => {
  try {
    course.value = await loadCourse(props);
    if (!course.value) throw new Error('Course not found.');
    let { latest, latestDraft, hasUnsavedChanges } = course.value;
    if (!latest?.courseId) throw new Error('Course not found.');

    lastUpdated.value = new Date(+latest.metadata.updatedDate);
    title.value = latest.title;
    hasSavedYaml.value = latest.yaml ? true : false;

    if (hasUnsavedChanges) {
      const draftAuthor = latestDraft?.author;
      throw new Error(`Course has unsaved changes from ${draftAuthor}.`);
    }

    const json = jsYaml.load(latest.yaml) as Content;
    let courseLangs = json.languages
      ? json.languages
      : json['default-language']
        ? [json['default-language']]
        : [];

    // we don't want to add en-GB to the languages list if it's not the base lang (e.g German-only courses)
    let isBaseLangEnglish =
      json['default-language'] && json['default-language'] !== 'en-GB'
        ? false
        : true;

    // if yaml doesn't contain languages, check the base course (no pages property means it's a full yaml and there is no base course)
    if (props.firmCode && !json?.pages && courseLangs.length === 0) {
      const baseCourseId = extractBaseCourse(latest.yaml) ?? props.courseId;
      const data = await getLatestSaved({ courseId: baseCourseId });
      const baseJson = jsYaml.load(data.yaml) as Content;
      isBaseLangEnglish =
        baseJson['default-language'] && baseJson['default-language'] !== 'en-GB'
          ? false
          : true;
      courseLangs = baseJson.languages
        ? baseJson.languages
        : baseJson['default-language']
          ? [baseJson['default-language']]
          : [];
    }

    // remove any en-GB from the languages array (it shouldn't be in there)
    courseLanguages.value = courseLangs.filter(l => l !== 'en-GB').sort();

    // if the base lang is English, include en-GB at the start of the lang list
    if (isBaseLangEnglish) {
      courseLanguages.value.unshift('en-GB');
    }

    // select the first language
    selectedLanguage.value = courseLanguages.value[0];

    // check if both the url and iframe are available
    watch(
      [url, courseTranscriptIFrame],
      async () => {
        if (url.value && courseTranscriptIFrame.value) {
          statuses.createTranscript = 'idle';
          loadCourseError.value = '';
        }
      },
      { immediate: true }
    );

    watch(
      () => selectedLanguage.value,
      () => {
        // force docx for unsupported PDF languages
        if (isUnsupportedPdfLanguage()) {
          fileType.value = 'docx';
        }
      }
    );
  } catch (e) {
    loadCourseError.value = e.message;
  }
};

onMounted(async () => {
  await load();
});

const docTitle = computed(() => `Create transcript ${baseYamlFilename.value}`);

useDocTitle(docTitle);
</script>

<style scoped></style>
